import { useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { type Column, type ColumnOrderState, flexRender, type Header, type Table } from "@tanstack/react-table";
import classNames from "classnames";

import { TableSeparatorIcon } from "../../../assets/icons";
import { Sorting } from "../Sorting";

import styles from "./DndHeader.module.scss";

const reorderColumn = (draggedColumnId: string, targetColumnId: string, columnOrder: string[]): ColumnOrderState => {
    columnOrder.splice(
        columnOrder.indexOf(targetColumnId),
        0,
        columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
    );
    return [...columnOrder];
};

export const DnDHeader: React.FC<{
    header: Header<any, unknown>;
    table: Table<any>;
    columnWidths: number[];
    index: number;
}> = ({ header, table, columnWidths, index }) => {
    const { getState, setColumnOrder } = table;
    const { columnOrder } = getState();
    const { column } = header;

    const [, dropRef] = useDrop({
        accept: "column",
        drop: (draggedColumn: Column<any>) => {
            const newColumnOrder = reorderColumn(draggedColumn.id, column.id, columnOrder);
            setColumnOrder(newColumnOrder);
        },
    });

    const [{ isDragging }, dragRef, previewRef] = useDrag({
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        item: () => column,
        type: "column",
    });

    const fixedPosition = useMemo(() => {
        return header.column.id === "_rowselection";
    }, [header]);
    return (
        <th
            key={header.id}
            className={classNames(styles.th)}
            style={{
                width: header.column.columnDef.size
                    ? header.column.columnDef.size === -1
                        ? "auto"
                        : header.column.columnDef.size
                    : columnWidths[index],
                opacity: isDragging ? 0.5 : 1,
            }}
            ref={dropRef}
        >
            <div className={styles["th-container"]} ref={previewRef}>
                <Sorting header={header}>{flexRender(header.column.columnDef.header, header.getContext())}</Sorting>
                {!fixedPosition && (
                    <div ref={dragRef} className={styles.handle}>
                        <TableSeparatorIcon />
                    </div>
                )}
            </div>
        </th>
    );
};
